/**
 * PortalSelect: Dropdown for selecting a merchant portal/store. Renders search (when >5 portals)
 * and grouped/flat portal options. Uses ResizeObserver for smooth height transitions on filter.
 */
import React from "react";
import {observer} from "mobx-react";
import {MerchantPortalDto, MerchantPortalId} from "../merchant-portal-menu-list-store";
import merchantStoresMenuStore from "../merchant-portal-menu-list-store";
import Option from "./select-parts/option";
import Group from "./select-parts/group";
import Search from "./select-parts/search";
import ToggleContents from "./select-parts/toggle-contents";
import {FONT_COLOR} from "../../../../../fonts/font-props";
import FontBody1 from "../../../../../fonts/font-body1/font-body1";
import HeaderMenu, { HEADER_MENU_VARIANT } from "../../header-menu/header-menu";
import rmsMixpanelStore from "../../../../../../stores/rms-mixpanel-store";

import "./portal-select.scss";


type PortalSelectProps = {
    onPortalChange: (portalId: MerchantPortalId) => void,
}

const PortalSelect: React.FC<PortalSelectProps> = observer(({ onPortalChange }) => {
    const onSelect = (selectedValue: MerchantPortalDto|null): void => {
        if (selectedValue != null) {
            onPortalChange(selectedValue.id);
        }
    }

    const merchantStores = merchantStoresMenuStore.portals;
    const isSearchable = merchantStoresMenuStore.portalsCount > 5;
    const hasMultiplePortalGroups = merchantStores.length >= 2;

    return (
        <div className="portal-selection">
            <HeaderMenu
                onToggle={() => rmsMixpanelStore.trackEvent('d&_header_select_store')} toggleContents={({ isOpen }) => <ToggleContents isMenuOpen={isOpen}/>} closeOnClick={false}
                variant={HEADER_MENU_VARIANT.PORTAL_SELECT_MENU}
                className="portal-selection__header-menu"
            >
                {
                    ({ setIsOpen }) => (
                        <div className="portal-selection__menu">
                            <div className="portal-selection__menu__content">
                                {isSearchable && <Search/>}
                                {hasMultiplePortalGroups && merchantStoresMenuStore.filteredPortals.map((portal) =>
                                (portal.portals.length > 0
                                        ? (
                                            <Group key={portal.id} mainPortal={portal}>
                                                {portal.getPortalsByLabel(merchantStoresMenuStore.lowercaseSearchValue).map(secondaryPortal => (
                                                    <Option key={secondaryPortal.id} portal={secondaryPortal} onSelect={() => {
                                                        setIsOpen(false);
                                                        onSelect(secondaryPortal);
                                                    }}/>
                                                ))}
                                            </Group>
                                        ) : (
                                            <Option key={portal.id} portal={portal} onSelect={() => {
                                                setIsOpen(false);
                                                onSelect(portal);
                                            }}/>
                                        )
                                )
                            )}
                            {!hasMultiplePortalGroups && merchantStoresMenuStore.filteredPortals.flatMap((portal) => {
                                if (portal.portals.length > 0) {
                                    return portal.portals.map((secondaryPortal) => (
                                        <Option key={secondaryPortal.id} portal={secondaryPortal} onSelect={() => {
                                            setIsOpen(false);
                                            onSelect(secondaryPortal);
                                        }}/>
                                    ));
                                }

                                return [<Option key={portal.id} portal={portal} onSelect={() => {
                                    setIsOpen(false);
                                    onSelect(portal);
                                }}/>];
                            })}
                                {merchantStoresMenuStore.filteredPortals.length === 0 && (
                                    <div className="portal-selection__menu__content__empty">
                                        <FontBody1 color={FONT_COLOR.PRIMARY3}>No portals found</FontBody1>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }
            </HeaderMenu>
        </div>
    );
});

export default PortalSelect;